import {
    Component,
    OnInit,
    ChangeDetectorRef,
    ViewChild,
    inject,
    computed,
    signal,
    input,
    output,
    effect,
    ChangeDetectionStrategy,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25Util } from "../../../../util/s25-util";
import { Budget } from "../Budget";
import { S25TableComponent } from "../../../s25-table/s25.table.component";
import { Table } from "../../../s25-table/Table";
import { GenericTableRenderHTMLComponent } from "../../../s25-table/generics/generic.table.render.html.component";
import { GenericTableActionsComponent, TableAction } from "../../../s25-table/generics/generic.table.actions.component";
import { Bind } from "../../../../decorators/bind.decorator";
import { BudgetPlanService } from "./budget.plan.service";
import { BudgetService } from "../budget.service";
import { S25TableModule } from "../../../s25-table/s25.table.module";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25PipesModule } from "../../../../pipes/s25.pipes.module";
import { S25LoadingInlineModule } from "../../../s25-loading/s25.loading.inline.module";
import { BudgetPlanModalComponent } from "./budget.plan.modal.component";
import { BudgetLineItemsComponent } from "./budget.line.items.component";
import { UserprefService } from "../../../../services/userpref.service";
import { S25PricingUtil } from "../../../s25-pricing/s25.pricing.util";
import { AlertService } from "../../../../services/alert.service";
import { CacheRepository } from "../../../../decorators/cache.decorator";
import { BudgetUtil } from "../budget.util";
import { BudgetLabelPhrases, BudgetLabels } from "../budget.labels";
import { SeOrgSecurityStore } from "../../se.org.security.store";
import { SeSecurityStore } from "../../se.security.store";
import { FormsModule } from "@angular/forms";

@TypeManagerDecorator("s25-ng-budget-plan")
@Component({
    selector: "s25-ng-budget-plan",
    template: `
        <div class="budget-plan-management">
            <!-- Header Section -->
            <div class="budget-plan-header">
                <h2>{{ labelPhrases.budgetManagement }}</h2>
                <p class="subtitle">{{ labelPhrases.manageBudgetsSubtitle }}</p>
            </div>

            <!-- Loading State -->
            @if (isLoading()) {
                <s25-ng-loading-inline-static [text]="labelPhrases.loadingBudgets"></s25-ng-loading-inline-static>
            }

            <!-- Error State -->
            @if (error()) {
                <div class="error-message">
                    <p>{{ error() }}</p>
                    <s25-ng-button
                        [type]="'outline'"
                        [buttonText]="'Retry'"
                        [onClick]="loadBudgetPlans"
                    ></s25-ng-button>
                </div>
            }

            <!-- Budget Plans Table -->
            @if (!isLoading() && !error()) {
                @if (!orgBudgets().length) {
                    <div class="empty-budgets">
                        <p>{{ labelPhrases.noBudgetsYetNeedAllocation }}</p>
                    </div>
                } @else {
                    <div class="budget-selector">
                        <label for="budgetPlanBudgetSelect" class="budget-selector-label">{{
                            labels.ALLOCATION
                        }}</label>
                        <select
                            id="budgetPlanBudgetSelect"
                            class="c-input"
                            [ngModel]="selectedBudgetId()"
                            (ngModelChange)="onSelectedBudgetChange($event)"
                        >
                            @for (orgBudget of orgBudgets(); track orgBudget.budgetId) {
                                <option [ngValue]="orgBudget.budgetId">
                                    {{ orgBudget.name }}
                                    @if (orgBudget.fiscalYear?.name) {
                                        ({{ orgBudget.fiscalYear?.name }})
                                    }
                                </option>
                            }
                        </select>
                    </div>

                    <div class="budget-plans-section">
                        <div class="section-header">
                            <h3>{{ labels.BUDGETS }}</h3>
                            @if (orgSecStore.canManageBudgetPlan()) {
                                <s25-ng-button
                                    [type]="'primary'"
                                    [buttonText]="labelPhrases.createBudget"
                                    [onClick]="createBudgetPlan"
                                    [isLoading]="isCreatingPlan()"
                                    [loadingText]="'Creating...'"
                                ></s25-ng-button>
                            }
                        </div>

                        <s25-ng-table
                            [dataSource]="tableConfig"
                            [columnSortable]="true"
                            [hasFilter]="true"
                            [hasRefresh]="true"
                            [hasSelect]="true"
                            [hasSelectAll]="true"
                            [hasExpandableRows]="true"
                            [(selected)]="selectedPlanIds"
                            (selectedChange)="onSelectionChange($event)"
                        ></s25-ng-table>
                    </div>
                }
            }

            <!-- Create/Edit Budget Plan Modal -->
            <s25-ng-budget-plan-modal
                #budgetPlanModal
                [orgId]="orgId()"
                [planId]="planId()"
                [preselectedBudget]="selectedBudget()"
                (planSaved)="onPlanSaved($event)"
                (planCancelled)="onPlanCancelled()"
            ></s25-ng-budget-plan-modal>
        </div>
    `,
    styles: `
        .budget-plan-management {
            max-width: 1200px;
            margin: 0 auto;
            padding: 2rem;
            background: var(--color--surface-bg);
            border-radius: 8px;
            box-shadow: 0 2px 8px color-mix(in srgb, var(--color--text) 12%, transparent);
        }

        .budget-plan-header {
            text-align: center;
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid var(--color--border);
        }

        .budget-plan-header h2 {
            margin: 0 0 0.5rem 0;
            color: var(--color--text);
            font-size: 1.8rem;
        }

        .budget-plan-header .subtitle {
            margin: 0;
            color: var(--color--text-muted);
            font-size: 1rem;
        }

        .error-message {
            text-align: center;
            padding: 1.5rem;
            background: var(--color--surface-bg-error);
            border: 1px solid color-mix(in srgb, var(--color--danger) 35%, var(--color--surface-bg));
            border-radius: 8px;
            margin: 1rem 0;
        }

        .error-message p {
            color: var(--color--danger);
            margin-bottom: 1rem;
        }

        .budget-selector {
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
            margin: 0 0 1.25rem;
            max-width: 480px;
        }

        .budget-selector-label {
            font-weight: 600;
            color: var(--color--text);
            font-size: 0.95rem;
        }

        .budget-selector .c-input {
            min-height: 2.4rem;
        }

        .empty-budgets {
            text-align: center;
            padding: 1.5rem;
            background: var(--color--surface-2-bg);
            border: 1px dashed var(--color--border);
            border-radius: 8px;
            color: var(--color--text-muted);
            margin: 1rem 0;
        }

        .budget-plans-section {
            margin-top: 2rem;
        }

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
            padding-bottom: 0.8rem;
            border-bottom: 1px solid var(--color--border);
        }

        .section-header h3 {
            margin: 0;
            color: var(--color--text);
            font-size: 1.4rem;
            font-weight: 600;
        }

        .bulk-actions {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
            margin-top: 1.5rem;
            padding-top: 1rem;
            border-top: 1px solid var(--color--border);
        }

        .selected-count {
            font-weight: 600;
            color: var(--color--text);
        }

        /* Status badges */
        :host ::ng-deep .status-badge {
            display: inline-block;
            padding: 0.25rem 0.75rem;
            border-radius: 12px;
            font-size: 0.85rem;
            font-weight: 600;
            text-align: center;
            white-space: nowrap;
        }

        :host ::ng-deep .status-draft {
            background: color-mix(in srgb, var(--cn-blue) 18%, var(--color--surface-bg));
            color: var(--color--link-text-hover);
        }

        :host ::ng-deep .status-in-progress {
            background: color-mix(in srgb, var(--color--requested-changes) 22%, var(--color--surface-bg));
            color: var(--color--text-warning);
        }

        :host ::ng-deep .status-approved {
            background: color-mix(in srgb, var(--color--objects-event) 18%, var(--color--surface-bg));
            color: var(--color--objects-event);
        }

        :host ::ng-deep .status-denied {
            background: color-mix(in srgb, var(--color--danger) 18%, var(--color--surface-bg));
            color: var(--color--danger);
        }

        :host ::ng-deep .status-changes-requested {
            background: color-mix(in srgb, var(--color--objects-resource) 18%, var(--color--surface-bg));
            color: var(--color--objects-resource);
        }

        :host ::ng-deep .status-unknown {
            background: var(--color--surface-2-bg);
            color: var(--color--text-muted);
        }

        @media (max-width: 768px) {
            .budget-plan-management {
                padding: 1rem;
                margin: 0;
                border-radius: 0;
            }

            .budget-plan-header {
                margin-bottom: 1rem;
                padding-bottom: 0.75rem;
            }

            .budget-plan-header h2 {
                font-size: 1.4rem;
            }

            .budget-plan-header .subtitle {
                font-size: 0.9rem;
            }

            .budget-plans-section {
                margin-top: 1rem;
            }

            .section-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
                margin-bottom: 1rem;
                padding-bottom: 0.5rem;
            }

            .section-header h3 {
                font-size: 1.2rem;
                text-align: center;
            }
        }
    `,
    standalone: true,
    imports: [
        FormsModule,
        S25TableModule,
        S25ButtonComponent,
        S25PipesModule,
        S25LoadingInlineModule,
        BudgetPlanModalComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [BudgetPlanService],
})
export class BudgetPlanComponent implements OnInit {
    readonly labels = BudgetLabels;
    readonly labelPhrases = BudgetLabelPhrases;

    readonly orgId = input.required<number>();
    /** When set, used by the plan modal for edit-mode context. */
    readonly planId = input<number | undefined>(undefined);
    readonly budgetId = input<number | undefined>(undefined);

    readonly planSaved = output<Budget.BudgetPlan>();
    readonly planCancelled = output<void>();
    readonly budgetIdChange = output<number | undefined>();

    @ViewChild("budgetPlanModal") budgetPlanModal: BudgetPlanModalComponent;
    @ViewChild(S25TableComponent) tableComponent: S25TableComponent;

    orgSecStore = inject(SeOrgSecurityStore);
    seSecurityStore = inject(SeSecurityStore);

    init = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    isCreatingPlan = signal<boolean>(false);
    orgBudgets = signal<Budget.OrgBudget[]>([]);
    selectedBudgetId = signal<number | undefined>(undefined);
    selectedBudget = computed<Budget.OrgBudget | undefined>(() => {
        const budgetId = this.selectedBudgetId();
        if (budgetId == null) return undefined;
        return this.orgBudgets().find((orgBudget) => orgBudget.budgetId === budgetId);
    });
    error = computed(() => {
        if (this.seSecurityStore.loadedOrgs().has(this.orgId()) && !this.orgSecStore.canViewBudget()) {
            return BudgetLabelPhrases.noPermissionViewBudgets;
        }
        return null;
    });
    budgetPlans: Budget.BudgetPlan[] = [];
    selectedPlanIds: Set<number> = new Set();
    selectedPlans: Budget.BudgetPlan[] = [];

    tableConfig: Table.DataSource;

    private readonly syncBudgetIdFromParent = effect(() => {
        const parentBudgetId = this.budgetId();
        if (parentBudgetId == null) {
            return;
        }
        if (parentBudgetId === this.selectedBudgetId()) {
            return;
        }
        this.selectedBudgetId.set(parentBudgetId);
        this.tableComponent?.refresh(true);
    });

    constructor(private cd: ChangeDetectorRef) {}

    async ngOnInit(): Promise<void> {
        this.orgSecStore.setOrgId(this.orgId());

        this.initTableConfig();
        await this.loadOrgBudgets();
        this.loadBudgetPlans();
    }

    private async loadOrgBudgets(): Promise<void> {
        const [budgets, fetchError] = await S25Util.Maybe(BudgetService.getAllOrgBudgets(this.orgId()));
        if (fetchError) {
            console.error("Error loading organization budgets", fetchError);
            this.orgBudgets.set([]);
            return;
        }

        const safeBudgets = budgets || [];
        this.orgBudgets.set(safeBudgets);

        const preferredId = this.budgetId() ?? this.selectedBudgetId();
        const stillAvailable = safeBudgets.some((orgBudget) => orgBudget.budgetId === preferredId);
        const nextSelectedId = stillAvailable ? preferredId : safeBudgets[0]?.budgetId;

        if (nextSelectedId !== this.selectedBudgetId()) {
            this.selectedBudgetId.set(nextSelectedId);
            this.budgetIdChange.emit(nextSelectedId);
        }
    }

    onSelectedBudgetChange(budgetId: number | undefined): void {
        if (budgetId === this.selectedBudgetId()) return;
        this.selectedBudgetId.set(budgetId);
        this.budgetIdChange.emit(budgetId);
        this.tableComponent?.refresh(true);
    }

    private initTableConfig(): void {
        const columns: Table.Column[] = [
            { id: "name", header: "Plan Name", minWidth: 150 },
            { id: "planType", header: "Plan Type", width: 120 },
            { id: "status", header: "Status", width: 130 },
            { id: "allocatedAmount", header: "Allocated", width: 120, align: "right" },
            { id: "description", header: "Description", minWidth: 200 },
            { id: "actions", header: "Actions", width: 200, sortable: false },
        ];

        this.tableConfig = {
            type: "unpaginated",
            columns: columns,
            dataSource: this.getBudgetPlans,
        };
    }

    @Bind
    async getBudgetPlans(query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        try {
            if (query.forceRefresh) {
                CacheRepository.invalidateByService("BudgetPlanService", "getBudgetPlans");
            }
            const dateFormat = await UserprefService.getS25Dateformat();
            const [data, error] = await S25Util.Maybe(BudgetPlanService.getBudgetPlans(this.orgId()));

            if (error) {
                throw error;
            }

            const activeBudgetId = this.selectedBudgetId();
            this.budgetPlans = (data || []).filter(
                (plan) => activeBudgetId == null || plan.budgetId === activeBudgetId,
            );

            // Load line items for each budget plan
            const rowsWithLineItems = await Promise.all(
                this.budgetPlans.map(async (plan) => {
                    const statusInfo = BudgetUtil.getStatusInfo(plan.workflowStatus);
                    const allocatedAmount = BudgetUtil.getAmount(plan);

                    // Determine if budget is a draft
                    const isDraft = BudgetUtil.isDraft(plan);
                    const canEdit = BudgetUtil.canEdit(plan);
                    const hasRequestedChanges = BudgetUtil.hasRequestedChanges(plan);

                    // Build className for row styling
                    let rowClassName = "";
                    if (hasRequestedChanges) {
                        rowClassName = "has-requested-changes";
                    }

                    return {
                        id: plan.budgetPlanId,
                        name: plan.name,
                        expandable: true,
                        className: rowClassName,
                        cells: {
                            name: { text: plan.name },
                            planType: { text: plan.planType },
                            status: {
                                component: GenericTableRenderHTMLComponent,
                                inputs: {
                                    text: `<span class="status-badge status-${statusInfo.class}">${statusInfo.label}</span>`,
                                    isHtml: true,
                                },
                                textValue: statusInfo.label,
                            },
                            allocatedAmount: {
                                text: S25PricingUtil.formatCurrency(allocatedAmount, plan.currency),
                            },
                            description: { text: plan.description || "" },
                            actions: {
                                component: GenericTableActionsComponent,
                                inputs: {
                                    actions: this.getActions(plan, isDraft, canEdit),
                                },
                            },
                        },
                        expandedContent: {
                            component: BudgetLineItemsComponent,
                            inputs: {
                                budgetPlan: plan,
                            },
                            outputs: {},
                        },
                        data: plan, // Store plan data for template access
                    } as Table.Row;
                }),
            );

            return {
                rows: rowsWithLineItems,
                totalRows: rowsWithLineItems.length,
            };
        } catch (error) {
            console.error("Error loading budget plans:", error);
            return {
                rows: [],
                totalRows: 0,
            };
        }
    }

    async loadBudgetPlans(): Promise<void> {
        this.isLoading.set(true);
        this.cd.detectChanges();

        try {
            await this.tableComponent?.refresh(true);
        } finally {
            this.isLoading.set(false);
            this.cd.detectChanges();
        }
    }

    @Bind
    async createBudgetPlan(): Promise<void> {
        this.isCreatingPlan.set(true);
        this.cd.detectChanges();

        try {
            this.budgetPlanModal.openCreateModal();
        } catch (error) {
            S25Util.showError(error, BudgetLabelPhrases.failedOpenBudgetForm);
        } finally {
            this.isCreatingPlan.set(false);
            this.cd.detectChanges();
        }
    }

    async editBudgetPlan(plan: Budget.BudgetPlan): Promise<void> {
        this.budgetPlanModal.openEditModal(plan);
    }

    @Bind
    async submitBudgetPlan(plan: Budget.BudgetPlan): Promise<void> {
        try {
            const confirmed = await AlertService.confirm(
                `Are you sure you want to submit "${plan.name}" for approval? You will not be able to edit it after submission.`,
            );

            if (!confirmed) return;

            const [_, error] = await S25Util.Maybe(BudgetPlanService.submitBudgetPlanForApproval(plan.budgetPlanId));

            if (error) {
                S25Util.showError(error, BudgetLabelPhrases.failedSubmitBudget);
                return;
            }

            alert(BudgetLabelPhrases.budgetSubmittedSuccessfully);
            await this.tableComponent?.refresh(true);
        } catch (error) {
            S25Util.showError(error, BudgetLabelPhrases.failedSubmitBudget);
        }
    }

    private getActions(plan: Budget.BudgetPlan, isDraft: boolean, canEdit: boolean): TableAction[] {
        const actions: TableAction[] = [];
        const canManage = this.orgSecStore.canManageBudgetPlan();

        // Only show edit/submit actions if user has manage permissions
        if (canManage && canEdit) {
            actions.push({
                label: "Edit",
                type: "outline",
                onClick: () => this.editBudgetPlan(plan),
                visible: true,
            });
        }

        if (canManage && canEdit && BudgetUtil.isDraft(plan)) {
            actions.push({
                label: BudgetUtil.shouldShowSubmitAction(plan, canManage) ? "Submit for Approval" : "Submit",
                type: "primary",
                onClick: () => this.submitBudgetPlan(plan),
                visible: true,
            });
        }

        if (canManage && canEdit && BudgetUtil.shouldShowResubmitAction(plan, canManage)) {
            actions.push({
                label: "Resubmit",
                type: "primary",
                onClick: () => this.submitBudgetPlan(plan),
                visible: true,
            });
        }

        return actions;
    }

    onSelectionChange(selectedIds: Set<string | number>): void {
        // Filter to only numbers since our plan IDs are numbers
        const numericIds = new Set<number>();
        selectedIds.forEach((id) => {
            if (typeof id === "number") {
                numericIds.add(id);
            }
        });
        this.selectedPlanIds = numericIds;
        this.selectedPlans = this.budgetPlans.filter((plan) => numericIds.has(plan.budgetPlanId));
    }

    onPlanSaved(plan: Budget.BudgetPlan): void {
        this.planSaved.emit(plan);
        // Refresh the table after a plan is saved
        this.tableComponent?.refresh(true);
    }

    onPlanCancelled(): void {
        this.planCancelled.emit();
    }
}
